import { HttpTypes } from '@medusajs/types';
import { ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export enum BusinessMode {
  ALONE = 'alone',
  B2C = 'b2c',
  B2B = 'b2b',
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function applyTranslations<T>(node: T, countryCode: string): T {
  if (Array.isArray(node)) {
    return node.map((item) => applyTranslations(item, countryCode)) as T
  } else if (node && typeof node === "object") {
    const obj = node as Record<string, any>
    // If translations exist, replace matching keys
    if (
      Object.prototype.hasOwnProperty.call(obj, "translations") &&
      typeof obj.translations === "object"
    ) {
      const translations = obj.translations[countryCode] || obj.translations
      for (const key in translations) {
        if (!Object.prototype.hasOwnProperty.call(obj, key)) continue
        const val = translations[key]
        // Never wipe existing content with empty/missing locale strings
        if (val === null || val === undefined || val === "") continue
        obj[key] = val
      }
    }
    // Recursively apply to all properties
    for (const key in obj) {
      if (key !== "translations" && obj[key] && typeof obj[key] === "object") {
        obj[key] = applyTranslations(obj[key], countryCode)
      }
    }
    return obj as T
  }
  return node
}

export class windowStorage {
  static set(key: string, value: any, ttl: number = 0) {
    const data = {
      value,
      _expire: ttl ? Date.now() + ttl * 1000 : null
    }
    localStorage.setItem(key, JSON.stringify(data))
  }
  static get(key: string) {
    const item = localStorage.getItem(key)
    if (!item) return null

    try {
      const data = JSON.parse(item)
      if (data._expire && Date.now() > data._expire) {
        localStorage.removeItem(key)
        return null
      }
      return data.value
    } catch {
      return null
    }
  }
  static remove(key: string) {
    localStorage.removeItem(key)
  }
}


export const optionsAsKeymap = (
  variantOptions: HttpTypes.StoreProductVariant["options"]
) => {
  return variantOptions?.reduce(
    (
      acc: Record<string, string>,
      varopt: HttpTypes.StoreProductOptionValue
    ) => {
      acc[varopt.option?.title.toLowerCase() || ""] = varopt.value

      return acc
    },
    {}
  )
}

/** Merge default priced variant options with URL search params. */
export const buildSelectedVariantOptions = (
  product: HttpTypes.StoreProduct,
  searchParams: Record<string, string> = {}
) => {
  const cheapestWithPrice = product.variants
    ?.filter((v) => v.calculated_price)
    .sort(
      (a, b) =>
        (a.calculated_price?.calculated_amount ?? 0) -
        (b.calculated_price?.calculated_amount ?? 0)
    )[0]

  return {
    ...optionsAsKeymap(cheapestWithPrice?.options ?? null),
    ...searchParams,
  }
}

export const resolveVariantId = (
  product: HttpTypes.StoreProduct,
  selectedOptions: Record<string, string>
) => {
  return (
    product.variants?.find((variant) =>
      variant.options?.every((option) => {
        const key = option.option?.title.toLowerCase()
        if (!key) {
          return false
        }
        const selected = selectedOptions[key]
        return selected != null && selected === option.value
      })
    )?.id ?? ""
  )
}

export const throttle = <T extends (...args: any[]) => any>(
  fn: T,
  time: number,
  options: { trailing?: boolean } = {}
) => {
  let timer: NodeJS.Timeout | null = null
  let lastArgs: Parameters<T> | null = null

  return (...args: Parameters<T>): void => {
    if (timer) {
      lastArgs = args
      return
    }

    fn(...args)
    timer = setTimeout(() => {
      if (options.trailing && lastArgs) {
        fn(...lastArgs)
        lastArgs = null
      }
      timer = null
    }, time)
  }
}


export const isDev = process.env.APP_MODE === "dev"

export const isAlone = process.env.NEXT_PUBLIC_BUSINESS_MODE === BusinessMode.ALONE;
export const isB2C = process.env.NEXT_PUBLIC_BUSINESS_MODE === BusinessMode.B2C;
export const isB2B = process.env.NEXT_PUBLIC_BUSINESS_MODE === BusinessMode.B2B;

/**
 * Convert a string to URL-friendly slug format
 * - Converts to lowercase
 * - Replaces spaces and '&' with hyphens
 * - Removes special characters except hyphens
 * - Removes consecutive hyphens
 */
export function slugify(text: string): string {
  if (!text) return ''

  return text
    .toString()
    .toLowerCase()
    .trim()
    // Replace '&' with 'and'
    .replace(/&/g, '-and-')
    // Replace spaces with hyphens
    .replace(/\s+/g, '-')
    // Remove all non-word chars except hyphens
    .replace(/[^\w\-]+/g, '')
    // Replace multiple hyphens with single hyphen
    .replace(/\-\-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+/, '')
    .replace(/-+$/, '')
}